import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { BookOpen, Clock, Sparkles, Download, Bookmark, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ShareDialog } from './ShareDialog';
import { useBookmark } from '../hooks/useBookmark';
import { useGenerateBook } from '../hooks/useGenerateBook';

export function BookDetailsModal({ book, isOpen, onClose, onSelectBook, isSaved: isSavedProp }) {
  // Always call hooks first - they must be called in the same order every render
  const { user } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(60);

  // Bookmark logic (skipLibraryCheck=true — parent passes isSaved via prop)
  const { isSaved, isSaving, toggle: handleBookmark, setIsSaved } = useBookmark(book || { id: 0 }, {
    initialSaved: isSavedProp,
    skipLibraryCheck: true,
  });

  const { start: startGenerate } = useGenerateBook();

  // Simulate download progress
  React.useEffect(() => {
    if (!isDownloading) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });

      setTimeRemaining((prev) => {
        const newTime = Math.max(0, prev - 0.6);
        return newTime;
      });
    }, 600);

    return () => clearInterval(interval);
  }, [isDownloading]);

  // Early return check AFTER all hooks are declared
  if (!book) return null;

  const handleAddToLibrary = async () => {
    setIsDownloading(true);
    setProgress(0);
    setTimeRemaining(60);

    const result = await startGenerate(book);

    if (result.ok) {
      setIsSaved(true);
      onClose();
    } else {
      setIsDownloading(false);
    }
  };

  const getStatusBadge = () => {
    if (book.status === 'complete') {
      return <Badge variant="default" className="gap-1"><Sparkles className="h-3 w-3" /> Ready</Badge>;
    }
    if (book.status === 'processing') {
      return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Processing</Badge>;
    }
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-serif mb-2">{book.title}</DialogTitle>
              <DialogDescription className="text-lg">by {book.author}</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge()}
              {user && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleBookmark}
                    disabled={isSaving}
                    aria-label={isSaved ? 'Remove from library' : 'Save to library'}
                  >
                    <Bookmark
                      className={`h-5 w-5 ${isSaved ? 'fill-current text-primary' : ''}`}
                    />
                  </Button>
                  <ShareDialog book={book}>
                    <Button variant="ghost" size="icon" aria-label="Share">
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </ShareDialog>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Book Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Category</h3>
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span>Classic Literature</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Language</h3>
              <span>{book.language === 'en' ? 'English' : book.language}</span>
            </div>

            {book.status === 'complete' && book.audio_duration && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Duration</h3>
                <span>{Math.floor(book.audio_duration / 60)} minutes</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {book.status === 'complete' ? (
              <Button
                onClick={() => {
                  onSelectBook(book.id);
                  onClose();
                }}
                className="w-full"
                size="lg"
              >
                <BookOpen className="mr-2 h-5 w-5" />
                Read Now
              </Button>
            ) : (book.status === 'processing' || isDownloading) ? (
              <>
                <div className="text-center text-sm text-muted-foreground mb-2">
                  downloading - {Math.ceil(timeRemaining)}min
                </div>
                <Progress value={progress} className="w-full" />
              </>
            ) : (
              <Button
                onClick={handleAddToLibrary}
                className="w-full"
                size="lg"
              >
                <Download className="mr-2 h-5 w-5" />
                Add to Library
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
